// SendInquiryModal (Milestone 9). Opened from the "Send Inquiry" CTA. Shows a
// compact review of the area + selected products + estimate, collects an
// optional plain-text "Additional Details" note (no RTF in MVP), and submits
// the inquiry. On success the parent navigates to the thank-you page.
//
// Submission inputs come from the existing slices: the verified-visitor cookie
// (contact, sent automatically), geometryToken (area stats), and the product
// selections.
import { useState, useEffect, useMemo } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import {
  Dialog, DialogTitle, DialogContent, DialogActions,
  Box, Typography, TextField, Button, Alert, CircularProgress, Divider,
} from '@mui/material';
import { submitInquiry } from '../../store/inquiry-slice.js';
import { showSnackbar } from '../../store/snackbar-slice.js';
import { estimateRangeText } from '../../utils/estimate-format.js';
import { buildSelectionSummary } from '../../utils/selection-summary.js';
import SelectionSummaryList from './SelectionSummaryList.jsx';

const DETAILS_MAX_LENGTH = 5000;

const fmt = (n) => (n == null ? '—' : Number(n).toLocaleString());

const SummaryRow = ({ label, value }) => (
  <Box sx={{ display: 'flex', justifyContent: 'space-between', gap: 1 }}>
    <Typography variant="body2" color="text.secondary">{label}</Typography>
    <Typography variant="body2" sx={{ fontWeight: 600, textAlign: 'right' }}>{value}</Typography>
  </Box>
);

const SendInquiryModal = ({ open, onClose, onSuccess }) => {
  const dispatch = useDispatch();
  const [details, setDetails] = useState('');

  const area = useSelector((s) => s.area.stats.data);
  const areaType = useSelector((s) => s.area.areaType);
  const items = useSelector((s) => s.selections.items);
  const estimate = useSelector((s) => s.selections.estimate.data);
  const catalog = useSelector((s) => s.catalog.data);
  const { submitting, error, submitted } = useSelector((s) => s.inquiry);

  // Itemized, category-grouped view of the current selections — mirrors the
  // confirmation email so the user verifies exactly what they're sending.
  const summaryGroups = useMemo(
    () => buildSelectionSummary(catalog, items),
    [catalog, items],
  );

  // Once the thunk reports success, hand control back to the parent (navigates
  // to /thank-you). Guarded on `open` so a stale success can't re-trigger.
  useEffect(() => {
    if (open && submitted) {
      dispatch(showSnackbar({ message: 'Your inquiry has been sent.', severity: 'success' }));
      onSuccess();
    }
  }, [open, submitted, onSuccess, dispatch]);

  // Surface submit failures as a toast in addition to the inline alert, so the
  // feedback is visible even if the modal has scrolled.
  useEffect(() => {
    if (open && error) {
      dispatch(showSnackbar({ message: error, severity: 'error' }));
    }
  }, [open, error, dispatch]);

  const handleSubmit = (event) => {
    event.preventDefault();
    const selections = items.map((item) => (
      item.optionIds.length > 0
        ? { productId: item.productId, optionIds: item.optionIds }
        : { productId: item.productId }
    ));
    dispatch(submitInquiry({
      geometryToken: area?.geometryToken,
      selections,
      detailsText: details.trim() || undefined,
      // Only custom uploads round-trip their geometry: the server recomputes
      // county/city tiles from the signed area_ref_id, but a custom polygon
      // exists only client-side, so it must be sent back (verified against the
      // token's geomHash) to be persisted and mapped.
      displayGeoJson: areaType === 'custom' ? area?.displayGeoJson : undefined,
    }));
  };

  return (
    <Dialog
      open={open}
      onClose={submitting ? undefined : onClose}
      aria-labelledby="send-inquiry-title"
      fullWidth
      maxWidth="sm"
    >
      <DialogTitle id="send-inquiry-title">Send your inquiry</DialogTitle>
      <Box component="form" onSubmit={handleSubmit} noValidate>
        <DialogContent dividers sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
          <Typography variant="body2" color="text.secondary">
            Review your selections and add anything else we should know. Our team
            will follow up with a formal proposal.
          </Typography>

          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.75 }}>
            <SummaryRow label="Project area" value={area?.name || '—'} />
            <SummaryRow label="Area size" value={`${fmt(area?.areaSqMi)} sq mi`} />
            <SummaryRow label="Est. population" value={fmt(area?.estPopulation)} />
          </Box>

          <SelectionSummaryList groups={summaryGroups} />

          <Box>
            <Divider sx={{ mb: 1 }} />
            <SummaryRow
              label="Estimated investment"
              value={estimateRangeText(estimate) ?? '—'}
            />
          </Box>

          <TextField
            id="inquiry-details"
            label="Additional details (optional)"
            value={details}
            onChange={(e) => setDetails(e.target.value)}
            multiline
            minRows={4}
            fullWidth
            slotProps={{ htmlInput: { maxLength: DETAILS_MAX_LENGTH } }}
            helperText={`${details.length} / ${DETAILS_MAX_LENGTH}`}
            placeholder="Tell us about your timeline, specific requirements, or any questions."
          />

          {error && (
            <Alert severity="error" role="alert">{error}</Alert>
          )}
        </DialogContent>
        <DialogActions sx={{ px: 3, py: 2 }}>
          <Button onClick={onClose} disabled={submitting} color="inherit">
            Cancel
          </Button>
          <Button type="submit" variant="contained" disabled={submitting}>
            {submitting ? <CircularProgress size={22} color="inherit" /> : 'Send Inquiry'}
          </Button>
        </DialogActions>
      </Box>
    </Dialog>
  );
};

export default SendInquiryModal;
